import { Input, PolicyDispatchingPermission, assets } from '@pulseops/common'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import PDTable from './components/PDTable'
import PolicyDispatchingDialog from './components/PolicyDispatchingDialog'
import { ActivityIndicator, View, Text } from 'react-native'
import { ZIO } from '@mxt/zio'
import { pipe } from 'fp-ts/function'
import { borderedInputStyle } from './styles/policyDispatchingStyles'
import {
  PolicyDispatchingService,
  PoliciesByAgentSearchItem,
  PoliciesByAgentSearchResponse
} from './services/PolicyDispatchingService'
import { getACKStatus, getPermissions } from './utils/policyDispatchingUtils'

const headCell = [
  { id: 1, label: 'STT' },
  { id: 2, label: 'Số hợp đồng' },
  { id: 3, label: 'Số HSYCBH' },
  { id: 4, label: 'Ngày phát hành' },
  { id: 5, label: 'Tình trạng' },
  { id: 6, label: 'Định kỳ phí' },
  { id: 7, label: 'Chủ hợp đồng' },
  { id: 8, label: 'Ngày nhập kho' },
  { id: 9, label: 'Văn phòng nhập kho' },
  { id: 10, label: 'Ghi chú' }
]

const rowCell = [
  { id: 1, label: 'no' },
  { id: 2, label: 'policyNumber' },
  { id: 3, label: 'insuranceNumber' },
  { id: 4, label: 'releaseDate' },
  { id: 5, label: 'status' },
  { id: 6, label: 'regularFee' },
  { id: 7, label: 'contractOwner' },
  { id: 8, label: 'entryDate' },
  { id: 9, label: 'office' },
  { id: 10, label: 'note' }
]

type PolicyRow = {
  no: string
  policyNumber: string
  insuranceNumber: string
  releaseDate: string
  status: string
  regularFee: string
  contractOwner: string
  entryDate: string
  office: string
  note: string
}

type FormValues = {
  agentNumber: string
  agentName: string
}

type PageInfo = {
  page: number
  totalPages: number
  totalElements: number
  first: boolean
  last: boolean
}

const hasPoliciesByAgentBody = (
  response: PoliciesByAgentSearchResponse
): response is Extract<PoliciesByAgentSearchResponse, { body: unknown }> => 'body' in response

export default function PDAgentContractScreen() {
  const { control, handleSubmit, getValues, setValue } = useForm<FormValues>({
    defaultValues: {
      agentNumber: '',
      agentName: ''
    }
  })
  const [rows, setRows] = React.useState<PolicyRow[]>([])
  const [pageInfo, setPageInfo] = React.useState<PageInfo | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [hasSearched, setHasSearched] = React.useState(false)
  const [modalInfo, setModalInfo] = React.useState({ title: '', message: '' })
  const [openModal, setOpenModal] = React.useState(false)
  const permissions = getPermissions()
  const allowSearch = permissions.includes(PolicyDispatchingPermission.QuickSearchSearch)

  const toRow = (item: PoliciesByAgentSearchItem, index: number, page: number, size: number): PolicyRow => ({
    no: String(page * size + index + 1),
    policyNumber: item.contractNumber || '',
    insuranceNumber: item.crossReferenceNumber || '',
    releaseDate: item.issueDate || '',
    status: getACKStatus(item.status),
    regularFee: item.billingFrequency || '',
    contractOwner: item.contractOwner || '',
    entryDate: item.stockInDate || '',
    office: item.office || '',
    note: item.notes || ''
  })

  const search = (values: FormValues, nextPage: number) => {
    if (!values.agentNumber) {
      setModalInfo({ title: 'Tìm kiếm', message: 'Vui lòng nhập mã số đại lý trước khi tìm kiếm.' })
      setOpenModal(true)
      return
    }

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.searchQuickPoliciesByAgent({ agentNumber: values.agentNumber, page: nextPage }),
      ZIO.tap((response) =>
        ZIO.effectTotal(() => {
          setHasSearched(true)
          if (!hasPoliciesByAgentBody(response)) {
            setRows([])
            setPageInfo(null)
            setIsLoading(false)
            setModalInfo({
              title: 'Tìm kiếm',
              message: 'Không tìm được thông tin theo yêu cầu'
            })
            setOpenModal(true)
            return
          }

          setRows(
            response.body.policies.map((item, index) => toRow(item, index, response.body.page, response.body.size))
          )
          setValue('agentName', response.body.agentName || '')
          setPageInfo({
            page: response.body.page,
            totalPages: response.body.totalPages,
            totalElements: response.body.totalElements,
            first: response.body.page === 0,
            last: response.body.page >= response.body.totalPages - 1
          })
          setIsLoading(false)
        })
      ),
      ZIO.catchAll((error) =>
        ZIO.effectTotal(() => {
          console.error('Unable to search policies by agent', error)
          setHasSearched(true)
          setRows([])
          setPageInfo(null)
          setIsLoading(false)
          setModalInfo({
            title: 'Tìm kiếm thất bại',
            message: 'Không thể tải danh sách hợp đồng. Vui lòng thử lại sau.'
          })
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const handleSearch = handleSubmit((values) => search(values, 0))

  return (
    <View style={{ ...styles.container, position: 'relative' } as any}>
      <PolicyDispatchingDialog
        visible={openModal}
        title={modalInfo.title}
        message={modalInfo.message}
        onClose={() => setOpenModal(false)}
      />
      {isLoading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#ED1B2E" />
        </View>
      )}
      <Text style={styles.title}>Tìm nhanh</Text>
      <View style={styles.wrapper}>
        <Text style={styles.subTitle}>Danh sách hợp đồng</Text>
        {hasSearched && rows.length === 0 && <Text style={styles.result}>Không tìm được thông tin theo yêu cầu</Text>}
        <Text style={styles.noted}>(* thông tin cần nhập)</Text>
        <View
          style={
            {
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 24
            } as any
          }
        >
          <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', gap: 12, width: '90%' } as any}>
            <View style={{ width: '20%' } as any}>
              <Controller
                name="agentNumber"
                control={control}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <Input
                    title={'Mã số đại lý'}
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    errorMessage={error?.message}
                    required
                    containerStyle={{ flex: 1 }}
                    inputStyle={borderedInputStyle}
                    filled
                  />
                )}
              />
            </View>
            <View style={{ width: '25%', marginTop: 22 } as any}>
              <Controller
                name="agentName"
                control={control}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <Input
                    title={''}
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    errorMessage={error?.message}
                    containerStyle={{ flex: 1 }}
                    inputStyle={borderedInputStyle}
                    filled
                    disabled
                    alwayShowUnderline
                  />
                )}
              />
            </View>
            <button
              style={isLoading || !allowSearch ? styles.disabledButton : styles.submitButton}
              type="button"
              onClick={handleSearch}
              disabled={isLoading || !allowSearch}
            >
              <span style={styles.mr_5}>
                <assets.SearchWhite />
              </span>
              Tìm
            </button>
          </View>
        </View>
        <PDTable
          headCell={headCell}
          rowCell={rowCell}
          data={rows}
          pagination={
            pageInfo
              ? {
                  page: pageInfo.page,
                  totalPages: pageInfo.totalPages,
                  totalElements: pageInfo.totalElements,
                  first: pageInfo.first,
                  last: pageInfo.last,
                  onPageChange: (nextPage) => search(getValues(), nextPage)
                }
              : undefined
          }
        />
      </View>
    </View>
  )
}

const styles: Record<string, any> = {
  container: {
    paddingTop: 0,
    paddingRight: 40,
    paddingBottom: 30,
    paddingLeft: 40,
    height: '100%',
    overflow: 'auto'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '20px'
  },
  wrapper: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
  },
  subTitle: {
    color: '#fff',
    fontSize: '18px',
    fontWeight: 'bold',
    paddingTop: 4,
    paddingRight: 12,
    paddingBottom: 4,
    paddingLeft: 12,
    borderRadius: 4,
    backgroundColor: '#e7000b',
    marginBottom: 16,
    width: 'fit-content'
  },
  noted: {
    fontSize: '12px',
    color: '#6a7282',
    fontStyle: 'italic',
    marginBottom: 12,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end'
  },
  result: {
    color: '#894b00',
    backgroundColor: '#fefce8',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#ffdf20',
    borderRadius: 4,
    paddingTop: 8,
    paddingRight: 16,
    paddingBottom: 8,
    paddingLeft: 16,
    fontSize: '14px',
    width: 'fit-content',
    marginBottom: 12
  },
  submitButton: {
    backgroundColor: '#ED1B2E',
    color: '#fff',
    borderWidth: 0,
    paddingTop: 6,
    paddingRight: 16,
    paddingBottom: 6,
    paddingLeft: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    fontWeight: '600'
  },
  disabledButton: {
    backgroundColor: '#e5e7eb',
    color: '#9ca3af',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#d1d5db',
    paddingTop: 6,
    paddingRight: 16,
    paddingBottom: 6,
    paddingLeft: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    fontWeight: '600',
    cursor: 'not-allowed',
    opacity: 0.7
  },
  mr_5: {
    marginRight: '5px'
  },
  loadingOverlay: {
    position: 'fixed' as const,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 2
  }
}
